import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function KidneyTransplantCostAbroadPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Kidney Transplant Cost Abroad – What to Expect | Kidney Patient Support';

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('description', 'Understand kidney transplant cost abroad including global cost comparisons, factors affecting pricing, and transparent guidance for international patients. No hidden fees.');
    updateMetaTag('keywords', 'kidney transplant cost abroad, international kidney transplant cost, transplant cost comparison, kidney transplant pricing overseas, affordable kidney transplant');
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
    updateLinkTag('canonical', 'https://kidneypatientsupport.com/kidney-transplant-cost-abroad');

    updateMetaTag('og:url', 'https://kidneypatientsupport.com/kidney-transplant-cost-abroad', true);
    updateMetaTag('og:title', 'Kidney Transplant Cost Abroad – What to Expect', true);
    updateMetaTag('og:description', 'Understand kidney transplant cost abroad including global cost comparisons, factors affecting pricing, and transparent guidance for international patients.', true);
    updateMetaTag('og:type', 'article', true);

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', 'Kidney Transplant Cost Abroad – What to Expect');
    updateMetaTag('twitter:description', 'Understand kidney transplant cost abroad including global cost comparisons, factors affecting pricing, and transparent guidance for international patients.');

    const scripts: HTMLScriptElement[] = [];

    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Kidney Transplant Cost Abroad – What to Expect",
      "description": "Understand kidney transplant cost abroad including global cost comparisons, factors affecting pricing, and transparent guidance for international patients.",
      "url": "https://kidneypatientsupport.com/kidney-transplant-cost-abroad",
      "inLanguage": "en",
      "isPartOf": { "@type": "WebSite", "url": "https://kidneypatientsupport.com/" },
      "publisher": { "@id": "https://kidneypatientsupport.com/#organization" }
    });
    document.head.appendChild(webPageScript);
    scripts.push(webPageScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kidneypatientsupport.com/" },
        { "@type": "ListItem", "position": 2, "name": "Kidney Transplant Cost Abroad", "item": "https://kidneypatientsupport.com/kidney-transplant-cost-abroad" }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    scripts.push(breadcrumbScript);

    const faqSchemaScript = document.createElement('script');
    faqSchemaScript.type = 'application/ld+json';
    faqSchemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(f => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer }
      }))
    });
    document.head.appendChild(faqSchemaScript);
    scripts.push(faqSchemaScript);

    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scripts.forEach(s => { if (s.parentNode) document.head.removeChild(s); });
    };
  }, []);

  const faqs = [
    {
      question: "Why does kidney transplant cost vary so much between countries?",
      answer: "Costs differ due to hospital infrastructure, labor costs, regulatory frameworks, and the number of intermediaries involved. Countries with lower operational costs and direct coordination models tend to offer more affordable programs."
    },
    {
      question: "Are lower-cost transplant programs less safe?",
      answer: "Not necessarily. Cost differences often reflect economic factors rather than quality differences. What matters is that the hospital is licensed, surgeons are qualified, and the program complies with local transplant regulations."
    },
    {
      question: "What is typically included in the quoted cost?",
      answer: "Most programs include pre-transplant evaluation, surgery, hospitalization, medical coordination, and initial post-operative care. Travel, accommodation, and long-term medications are usually separate expenses."
    },
    {
      question: "Can I get a personalized cost estimate?",
      answer: "Yes. After submitting an information request, we provide general cost guidance based on your situation. Final medical pricing is determined exclusively by the treating hospital after clinical evaluation."
    }
  ];

  const costData = [
    { country: "United States", range: "USD 300,000 – 500,000+", note: "Varies by insurance and hospital" },
    { country: "United Kingdom", range: "USD 50,000 – 80,000", note: "NHS-covered for residents; private varies" },
    { country: "India", range: "USD 15,000 – 30,000", note: "Hospital fees only; coordination separate" },
    { country: "Pakistan", range: "USD 15,000 – 25,000", note: "Hospital fees only; coordination separate" },
    { country: "Turkey", range: "USD 30,000 – 60,000", note: "Varies by facility" },
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png" alt="Kidney Patient Support" className="h-12 w-auto" />
          </Link>
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/ethics" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Ethics</Link>
            <Link to="/complete-guide-kidney-transplant-abroad" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Guide</Link>
            <Link to="/countries" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Countries</Link>
            <Link to="/pricing" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Pricing</Link>
            <Link to="/process" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Process</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</Link>
          </div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer" aria-label="Toggle menu">
            {isMobileMenuOpen ? <i className="ri-close-line text-2xl"></i> : <i className="ri-menu-line text-2xl"></i>}
          </button>
        </div>
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-[55] overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 py-3 border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/ethics" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 py-3 border-b border-gray-100 cursor-pointer">Ethics</Link>
              <Link to="/countries" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 py-3 border-b border-gray-100 cursor-pointer">Countries</Link>
              <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 py-3 border-b border-gray-100 cursor-pointer">Pricing</Link>
              <Link to="/process" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 py-3 border-b border-gray-100 cursor-pointer">Process</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 py-3 border-b border-gray-100 cursor-pointer">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bf653d43900b15fa72dcef2e8700fec8.jpg"
            alt="International kidney transplant cost comparison"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center py-32 w-full">
          <div className="inline-block bg-white/15 backdrop-blur-sm text-white px-6 py-3 rounded-full mb-8 border border-white/30">
            <span className="text-sm font-bold tracking-wide">INFORMATIONAL COST GUIDANCE</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
            Kidney Transplant Cost Abroad &ndash;<br />What to Expect
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-6 leading-relaxed max-w-3xl mx-auto">
            Understanding the <strong>cost of kidney transplant abroad</strong> is an important step in financial planning. This page provides estimated global comparisons and explains why pricing varies between countries.
          </p>
          <p className="text-white/80 text-sm">
            Estimates are for planning purposes only. Final pricing is determined by the treating hospital.
          </p>
        </div>
      </section>

      {/* Global Cost Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Estimated Kidney Transplant Cost by Country
          </h2>
          <p className="text-gray-700 text-lg text-center mb-12 max-w-3xl mx-auto">
            The following table provides a general overview of <strong>kidney transplant pricing overseas</strong>. These figures represent hospital-related medical costs and may not include travel, accommodation, or long-term medications.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full bg-white rounded-2xl overflow-hidden border border-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-teal-600 to-teal-700">
                  <th className="px-8 py-5 text-left text-white font-bold text-base">Country</th>
                  <th className="px-8 py-5 text-left text-white font-bold text-base">Estimated Range</th>
                  <th className="px-8 py-5 text-left text-white font-bold text-base">Notes</th>
                </tr>
              </thead>
              <tbody>
                {costData.map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50' : ''} hover:bg-teal-50/50 transition-colors`}>
                    <td className="px-8 py-5 font-semibold text-gray-900 text-base">{row.country}</td>
                    <td className="px-8 py-5 text-teal-700 font-bold text-base">{row.range}</td>
                    <td className="px-8 py-5 text-gray-600 text-sm">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-6 max-w-4xl mx-auto">
            <p className="text-gray-700 text-sm">
              <strong>Important:</strong> These are estimated ranges for general reference only. Actual costs depend on individual medical factors, hospital selection, and clinical evaluation. Our coordination program for <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> typically falls within USD 120,000 – 150,000 including coordination support.
            </p>
          </div>
        </div>
      </section>

      {/* Factors Affecting Cost */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            How Much Does Kidney Transplant Cost Overseas
          </h2>
          <p className="text-gray-700 text-lg text-center mb-6 max-w-3xl mx-auto">
            One of the most common questions patients ask is <strong>how much does kidney transplant cost overseas</strong>. The answer depends on several variables, and understanding these factors helps set realistic expectations when planning for international treatment.
          </p>
          <p className="text-gray-700 text-base text-center mb-12 max-w-3xl mx-auto">
            Several variables influence the <strong>international kidney transplant cost</strong> a patient may encounter. The sections below break down the primary factors that determine pricing across different countries and programs.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-hospital-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Hospital &amp; Facility Standards</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Accredited hospitals with advanced infrastructure may charge differently than smaller facilities. All hospitals in our network are licensed and meet regulatory standards.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-user-heart-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Patient Medical Condition</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Pre-existing conditions, overall health, and complexity of the case can affect the scope of treatment and therefore the final cost determined by the hospital.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-map-pin-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Country &amp; Regional Economics</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Labor costs, currency exchange rates, and local economic conditions significantly influence medical pricing across different regions.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-team-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Number of Intermediaries</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Programs involving multiple brokers and agents add commission layers that inflate the total cost. Direct coordination models reduce these markups.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-time-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Length of Hospital Stay</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Post-operative recovery time varies by patient. Extended hospitalization due to complications may increase the final cost.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-capsule-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Medication &amp; Follow-Up</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Immunosuppressive medications during hospital stay are typically included, but long-term medication costs after discharge are a separate ongoing expense.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Pricing Varies */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Why Kidney Transplant Pricing Varies Internationally
          </h2>
          <p className="text-gray-700 text-lg text-center mb-6 max-w-3xl mx-auto">
            The wide range in <strong>kidney transplant cost abroad</strong> can be confusing. Here is a straightforward explanation of the primary reasons.
          </p>

          <div className="space-y-8 mb-10">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Healthcare System Structure</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Countries with publicly funded healthcare systems (like the UK or Canada) may cover transplant costs for residents, but international patients face different pricing. In countries like India or Pakistan, private hospital costs reflect local economic conditions, making procedures more affordable for international patients without compromising medical standards.
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Coordination Model Differences</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Some providers use multi-layered agency structures where each intermediary adds a commission. Our approach uses direct coordination with licensed hospitals, which is one reason our <Link to="/pricing" className="text-teal-600 hover:text-teal-700 font-medium">transparent cost overview</Link> shows a lower range than many competitors. The medical care itself remains with qualified hospital teams regardless of the coordination model.
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Regulatory &amp; Compliance Costs</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Countries with stricter regulatory frameworks may have higher compliance costs built into medical pricing. This is not inherently negative— it reflects the investment in patient safety and legal protections. All hospitals in our network comply with applicable local transplant regulations.
              </p>
            </div>
          </div>

          {/* New expanded content */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Understanding the Full Cost Picture for Overseas Transplant</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                When patients research how much kidney transplant costs overseas, they often encounter a wide range of figures without context. The quoted price from a hospital typically covers the surgical procedure, hospitalization, and immediate post-operative care. However, the total out-of-pocket expense also includes international airfare, accommodation for the patient and a companion, visa fees, and long-term immunosuppressive medications after discharge. Patients should request a detailed breakdown from any provider and compare like-for-like inclusions rather than headline figures alone.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Currency Exchange and Payment Timing</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Currency fluctuations can meaningfully affect the final cost of a kidney transplant overseas. Patients paying in a foreign currency may benefit from or be disadvantaged by exchange rate movements between the time of inquiry and the time of payment. Some programs offer staged payment structures that help manage this risk. Our <Link to="/pricing" className="text-teal-600 hover:text-teal-700 font-medium">transparent cost overview</Link> explains the typical payment stages and what to expect at each phase of the coordination process.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Post-Transplant Costs Are a Long-Term Consideration</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                The cost of kidney transplant overseas does not end with the surgery. Lifelong immunosuppressive medications, regular blood tests, and periodic follow-up appointments with a nephrologist in the patient's home country represent ongoing expenses. In many cases, these post-transplant costs are still significantly lower than the cumulative expense of continued dialysis. For a detailed comparison of long-term financial implications, review our <Link to="/dialysis-vs-kidney-transplant-cost" className="text-teal-600 hover:text-teal-700 font-medium">dialysis vs transplant cost comparison</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Transparent Cost Explanation */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Our Approach to Transplant Cost Transparency
          </h2>
          <p className="text-gray-700 text-lg text-center mb-12 max-w-3xl mx-auto">
            We believe patients deserve clear, upfront information about what they can expect to pay. Here is how we approach cost guidance for patients considering a <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> or other destinations.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-checkbox-circle-fill text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">What We Do</h3>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-check-line text-teal-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Provide estimated cost ranges for planning purposes</span>
                </li>
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-check-line text-teal-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Clearly explain what is and is not included</span>
                </li>
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-check-line text-teal-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Disclose all coordination fees upfront</span>
                </li>
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-check-line text-teal-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Coordinate directly with hospitals to minimize intermediary costs</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-close-circle-fill text-red-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">What We Do Not Do</h3>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-close-line text-red-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Guarantee specific pricing outcomes</span>
                </li>
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-close-line text-red-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Hide fees or add undisclosed commissions</span>
                </li>
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-close-line text-red-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Make claims about cost savings compared to specific providers</span>
                </li>
                <li className="flex items-start text-gray-700 text-sm">
                  <i className="ri-close-line text-red-600 mt-0.5 mr-3 flex-shrink-0 font-bold"></i>
                  <span>Determine final medical pricing (this is the hospital’s role)</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/pricing" className="inline-flex items-center text-teal-600 font-semibold text-lg hover:text-teal-700 transition-colors cursor-pointer">
              <span>View Our Full Transparent Cost Overview</span>
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Kidney Transplant Cost Abroad: Common Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className={`bg-gray-50 rounded-2xl border transition-all ${openFaq === index ? 'border-teal-300 shadow-md' : 'border-gray-200'}`}>
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-100 transition-colors rounded-2xl">
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-xl`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 text-base leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Personalized Cost Guidance?
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10 max-w-3xl mx-auto">
            Every patient’s situation is unique. Submit an information request to receive general guidance about <strong>kidney transplant cost abroad</strong> based on your circumstances.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center bg-white text-teal-600 px-12 py-5 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors space-x-3 whitespace-nowrap shadow-2xl cursor-pointer mb-4">
            <span>Request Information</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
          <p className="text-white/80 text-sm mt-4">
            No guarantees. Medical decisions remain with licensed physicians.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-amber-50 border-t border-amber-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-700 text-sm leading-relaxed">
            <strong>Disclaimer:</strong> All cost figures on this page are estimated ranges for informational and planning purposes only. Final medical pricing is determined exclusively by the treating hospital after clinical evaluation. We do not provide medical advice, treatment, or outcome guarantees. All medical decisions are made by licensed physicians. This page does not constitute a binding price quote.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-white/90 text-base leading-relaxed max-w-4xl mx-auto">
              <strong>This website provides coordination and informational support services only.</strong><br />
              Medical decisions and procedures are handled exclusively by licensed healthcare providers.
            </p>
          </div>
          <div className="border-t border-white/20 pt-8 mb-8">
            <h4 className="text-white font-bold text-sm tracking-wide mb-4 text-center">CORE PAGES</h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <Link to="/pakistan" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Kidney Transplant in Pakistan</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Transparent Cost Overview</Link>
              <Link to="/process" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">How the Process Works</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethical Framework</Link>
              <Link to="/who-this-is-for" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Eligibility Guide</Link>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/80 text-sm mb-4 md:mb-0">&copy; 2025 Japan Ambis International. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethics</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
